import { getLogs } from "@/services/companyService"
import { CompanyLogsDataType } from "@/types/company"
import { formatDate } from "@/utils/formatDate"
import { useEffect, useState, useMemo } from "react"
import { useLoading } from "@/hooks/useLoading"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"
import { LogsFilter } from "../components/LogsFilter"

const LogCard = ({ log }: { log: CompanyLogsDataType }) => {
  return (
    <div className="card-elevated animate-fade-in space-y-2 p-4">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-foreground">{log.pes_nome}</span>
        <span className="text-xs text-muted-foreground">{formatDate(log.datetime)}</span>
      </div>
      <div>
        <p className="text-sm">
          <span className="font-semibold">Ação:</span> {log.acao}
        </p>
        <p className="text-sm text-muted-foreground">{log.detalhes}</p>
      </div>
    </div>
  )
}

export default function BackofficeLogs() {
  const [logs, setLogs] = useState<CompanyLogsDataType[]>([])
  const [search, setSearch] = useState("")
  const { showLoading, hideLoading } = useLoading()
  const { user } = useAuth()
  const [dataInicio, setDataInicio] = useState(() => {
    const today = new Date()
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    return firstDayOfMonth.toLocaleDateString("pt-BR")
  })
  const [dataFim, setDataFim] = useState(() => {
    const today = new Date()
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    return lastDayOfMonth.toLocaleDateString("pt-BR")
  })

  const formatDateToApi = (date: string) => {
    const [day, month, year] = date.split("/")
    return `${year}-${month}-${day}`
  }

  const isCompleteDate = (value: string) => value.replace(/\D/g, "").length === 8

  const handleClearDates = () => {
    setDataInicio("")
    setDataFim("")
    showLoading()
    getLogs(user.empId, {})
      .then(({ data }) => setLogs(data))
      .catch(console.error)
      .finally(hideLoading)
  }

  const handleFetchLogs = () => {
    if (!user) return
    showLoading()
    const params: Record<string, string> = {}
    if (isCompleteDate(dataInicio)) params.data_inicio = formatDateToApi(dataInicio)
    if (isCompleteDate(dataFim)) params.data_fim = formatDateToApi(dataFim)
    getLogs(user.empId, params)
      .then(({ data }) => {
        setLogs(data)
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        hideLoading()
      })
  }

  useEffect(() => {
    handleFetchLogs()
  }, [])

  const filteredLogs = useMemo(() => {
    if (!search) return logs
    const q = search.toLowerCase()
    return logs.filter(
      (log) =>
        log.pes_nome.toLowerCase().includes(q) ||
        log.detalhes.toLowerCase().includes(q) ||
        log.acao.toLowerCase().includes(q),
    )
  }, [logs, search])

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        title="Logs"
        icon="search-alt"
      />

      <div className="px-6">
        <LogsFilter
          search={search}
          onSearchChange={setSearch}
          dataInicio={dataInicio}
          onDataInicioChange={setDataInicio}
          dataFim={dataFim}
          onDataFimChange={setDataFim}
          onFetch={handleFetchLogs}
          onClear={handleClearDates}
        />
      </div>

      {/* Lista de Logs em Cards */}
      <div className="space-y-4 px-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Registro de Atividades</h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <i className="fi fi-ts-edit"></i>
            <span>Encontrados: {filteredLogs.length} Registros</span>
          </div>
        </div>
        <div className="space-y-4">
          {filteredLogs.map((log, index) => (
            <LogCard
              key={index}
              log={log}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
